import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Link } from 'lucide-react';
import { useDispatch } from 'react-redux';
import CommonHeader from '../../util_components/common_header';
import CustomModal from '../../util_components/custom_modal';
import * as actions from '../../../redux/action_creators';
import RunCard from './run_card';
import RunsTable, { RUN_STATUS_FILTERS } from './runs_table';
import RegistrationsTab from './registrations_tab';
import ActivityTab from './activity_tab';
import { get_domain } from '../../../utils/urls';
import { IST_ZONE } from './ist_time';
import {
  cancel_run,
  cancel_slot,
  delete_run,
  create_run,
  duplicate_run,
  get_event,
  publish_run,
  update_event,
  update_run,
  useEventsRequest,
  clean_message
} from './events_api';

/**
 * Event detail — /events/:slug. Runs newest-first, editing in the Manage modal.
 * Registrations renders empty until marketing-forms can write one; that is expected.
 */

const TABS = ['Runs', 'Registrations', 'Activity'];

const MAX_DUPLICATE_DATES = 4;

/**
 * Today in IST, because run_date IS an IST calendar date — the same zone every other
 * field on this screen is pinned to. Read in the browser's own zone it disagreed with
 * the field it gates: an operator in US Pacific at 18:00 on the 25th is already on the
 * 26th in IST, so the date input offered a date the server then rejected as past, and
 * Add Run defaulted to a run date that could never be published.
 */
const today_iso = () => moment().tz(IST_ZONE).format('YYYY-MM-DD');

/** The public landing page for this topic. get_domain() picks the host per environment. */
const event_page_url = (slug) => `${get_domain()}/events/${slug}`;

export default function EventDetail({ match, history }) {
  const { slug } = match.params;
  const [topic, setTopic] = useState(null);
  const [tab, setTab] = useState('Runs');
  const [editing_topic, setEditingTopic] = useState(false);
  const [topic_name, setTopicName] = useState('');
  const [adding_run, setAddingRun] = useState(false);
  const [new_run_date, setNewRunDate] = useState(today_iso());
  const [new_run_duration, setNewRunDuration] = useState(30);
  const [duplicating, setDuplicating] = useState(null);
  const [duplicate_dates, setDuplicateDates] = useState(['']);
  const [confirming_cancel, setConfirmingCancel] = useState(null);
  const [confirming_delete, setConfirmingDelete] = useState(null);
  const [confirming_slot_cancel, setConfirmingSlotCancel] = useState(null);
  // The run whose Manage modal is open, or null when none is.
  const [managing_run, setManagingRun] = useState(null);
  const [run_status_filter, setRunStatusFilter] = useState('all');
  // Bumped only when a refetch lands. Run cards key off it, so they reseed their
  // edit state when server data changed rather than on an unrelated render.
  const [sync_key, setSyncKey] = useState(0);
  // Reported by the open run card. Every destructive action below refetches the topic,
  // which remounts that card and resets its fields, so the confirmations have to say
  // that unsaved edits go with it.
  const [editor_dirty, setEditorDirty] = useState(false);
  const { run, loading } = useEventsRequest();
  const dispatch = useDispatch();

  const load = useCallback(async () => {
    const { data } = await run(() => get_event(slug));
    if (!data) return;
    setTopic(data.data);
    setSyncKey((previous) => previous + 1);
  }, [run, slug]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Shown in every confirmation that refetches the topic while the run editor is open.
   * The refetch remounts the card, so unsaved date, duration, time and link edits are
   * gone — previously without a word, and the host's likeliest next move was to Save
   * again believing the edits were still there.
   */
  const unsaved_edits_warning = editor_dirty ? (
    <div className="mt-2 text-sm" style={{ color: 'var(--state-warning-text)' }}>
      You have unsaved changes to this run. Continuing discards them — Save first if you want to
      keep them.
    </div>
  ) : null;

  const notify_success = (message) =>
    dispatch(actions.set_notification_variable(true, 'success', message));

  const submit_topic_name = async () => {
    const { data } = await run(() => update_event(topic.topic_uuid, { name: topic_name.trim() }));
    if (!data) return;
    setEditingTopic(false);
    notify_success('Event updated');
    load();
  };

  /** Extracts the slot uuids a rejection names, so the card can flag those rows. */
  const offenders_named_in = (run_uuid, message) =>
    (topic.runs.find((item) => item.run_uuid === run_uuid)?.slots || [])
      .map((slot) => slot.slot_uuid)
      .filter((uuid) => uuid && message.includes(uuid));

  const submit_new_run = async () => {
    const { data } = await run(() =>
      create_run(topic.topic_uuid, {
        run_date: new_run_date,
        duration_mins: new_run_duration,
        slots: []
      })
    );
    if (!data) return;
    setAddingRun(false);
    notify_success('Run created as a draft');
    load();
  };

  /**
   * Resolves to null on success, or the offending slot uuids so the card flags them.
   *
   * `reload: false` is for the save-then-publish sequence. load() bumps sync_key, which
   * remounts the run card — fine at the end of an action, fatal in the middle of one,
   * because the publish that follows would then report its rejected slots to a dead
   * instance and no row would be flagged. The publish step refetches instead.
   */
  const save_run = async (target, payload, { reload = true } = {}) => {
    const { error } = await run(() => update_run(target.run_uuid, payload), { silent: true });
    if (!error) {
      notify_success('Run saved');
      if (reload) load();
      return null;
    }

    dispatch(actions.set_notification_variable(true, 'error', clean_message(error)));
    return offenders_named_in(target.run_uuid, error);
  };

  /** Silent: the error names the blocking slots, which the card flags as rows. */
  const publish = async (target) => {
    const { error } = await run(() => publish_run(target.run_uuid), { silent: true });
    if (!error) {
      notify_success('Run published — it is now live');
      load();
      return null;
    }

    dispatch(actions.set_notification_variable(true, 'error', clean_message(error)));
    return offenders_named_in(target.run_uuid, error);
  };

  const confirm_cancel_slot = async () => {
    const target = confirming_slot_cancel;
    setConfirmingSlotCancel(null);
    const { data } = await run(() => cancel_slot(target.slot_uuid));
    if (!data) return;
    notify_success('Slot cancelled');
    load();
  };

  const confirm_delete_run = async () => {
    const target = confirming_delete;
    setConfirmingDelete(null);
    const { data } = await run(() => delete_run(target.run_uuid));
    if (!data) return;
    notify_success('Draft run deleted');
    load();
  };

  const confirm_cancel_run = async () => {
    const target = confirming_cancel;
    setConfirmingCancel(null);
    const { data } = await run(() => cancel_run(target.run_uuid));
    if (!data) return;
    notify_success('Run cancelled');
    load();
  };

  const submit_duplicate = async () => {
    const dates = duplicate_dates.map((d) => d.trim()).filter(Boolean);
    const { data } = await run(() => duplicate_run(duplicating.run_uuid, dates));
    if (!data) return;
    setDuplicating(null);
    notify_success(`${dates.length} draft run${dates.length === 1 ? '' : 's'} created`);
    load();
  };

  const open_duplicate = (target) => {
    setDuplicating(target);
    setDuplicateDates(['']);
  };

  const set_duplicate_date = (index, value) =>
    setDuplicateDates((prev) => prev.map((d, i) => (i === index ? value : d)));

  // Mirrors the server's rules so a host is told before the round trip: 1-4 dates,
  // no repeats, none before today. Today itself is allowed — run_date is a calendar
  // date and a slot later today is legitimately schedulable.
  const duplicate_dates_error = (() => {
    const filled = duplicate_dates.map((d) => d.trim()).filter(Boolean);
    if (filled.length === 0) return 'Pick at least one date';
    if (new Set(filled).size !== filled.length) return 'The same date is listed twice';
    if (filled.some((d) => d < today_iso())) return 'A target date is in the past';
    return '';
  })();

  const visible_runs = useMemo(() => {
    const all = topic?.runs || [];
    return run_status_filter === 'all'
      ? all
      : all.filter((item) => item.status === run_status_filter);
  }, [topic, run_status_filter]);

  // The open modal must track the refetched topic, or a Save would leave the modal
  // rendering the pre-save run while the table behind it shows the new one.
  const managing_live = managing_run
    ? (topic?.runs || []).find((item) => item.run_uuid === managing_run.run_uuid) || null
    : null;

  if (!topic) {
    return <CommonHeader title="Event" loading={loading} show_back />;
  }

  return (
    <CommonHeader title={topic.name} loading={loading} show_back>
      <div className="p-4">
        <div
          className="rounded-lg px-4 py-3 mb-4 flex items-start"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-card)'
          }}
        >
          <div>
            <button
              type="button"
              onClick={() => history.push('/events')}
              className="text-sm mb-1"
              style={{
                color: 'var(--state-info)',
                background: 'transparent',
                border: 'none',
                borderRadius: 0,
                padding: 0
              }}
            >
              ← Events
            </button>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-medium" style={{ color: 'var(--text-primary)' }}>
                {topic.name}
              </h2>
              {/* An anchor, not a button: middle-click and "copy link address" both matter
                  to a host checking what a campaign points at. */}
              <a
                href={event_page_url(topic.slug)}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open the landing page — ${event_page_url(topic.slug)}`}
                aria-label="Open the public landing page in a new tab"
                className="flex items-center"
                style={{ color: 'var(--state-info)', padding: 2, lineHeight: 0 }}
              >
                <Link size={16} />
              </a>
            </div>
            <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {topic.slug}
            </div>
          </div>
          <button
            type="button"
            className="ct-small-btn ml-auto"
            onClick={() => {
              setTopicName(topic.name);
              setEditingTopic(true);
            }}
          >
            Edit
          </button>
        </div>

        <div
          className="flex gap-6 mb-4"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setTab(name)}
              className="text-sm"
              style={{
                color: tab === name ? 'var(--brand-primary)' : 'var(--text-muted)',
                background: 'transparent',
                border: 'none',
                borderRadius: 0,
                padding: '0 0 8px',
                borderBottom:
                  tab === name ? '2px solid var(--brand-primary)' : '2px solid transparent',
                fontWeight: tab === name ? 500 : 400
              }}
            >
              {name}
            </button>
          ))}
        </div>

        {tab === 'Runs' && (
          <>
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                Runs
              </h3>

              <select
                value={run_status_filter}
                onChange={(e) => setRunStatusFilter(e.target.value)}
                aria-label="Filter runs by status"
                className="px-2 py-1 rounded text-sm"
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  color: 'var(--input-text)'
                }}
              >
                {RUN_STATUS_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {visible_runs.length} of {(topic.runs || []).length}
              </span>

              <button
                type="button"
                className="ct-small-btn ml-auto"
                onClick={() => setAddingRun(true)}
              >
                + Add Run
              </button>
            </div>

            {(topic.runs || []).length === 0 ? (
              <div
                className="rounded-lg p-6 text-center"
                style={{
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-muted)'
                }}
              >
                No runs yet. Add one to get started.
              </div>
            ) : (
              <RunsTable
                runs={visible_runs}
                onManage={setManagingRun}
                onDuplicate={open_duplicate}
                onCancelRun={setConfirmingCancel}
                onDeleteRun={setConfirmingDelete}
              />
            )}
          </>
        )}

        {/* Both take `topic` rather than refetching it: the detail screen already
            holds every run and slot, which is what lets Registrations label its
            count chips with slot times and Activity resolve an entity_uuid to a
            readable name without a second request. */}
        {tab === 'Registrations' && <RegistrationsTab topic={topic} />}
        {tab === 'Activity' && <ActivityTab topic={topic} />}
      </div>

      {/* The full run editor, kept off the list so the table stays scannable. */}
      <CustomModal
        show={!!managing_live}
        close={() => setManagingRun(null)}
        title={
          managing_live
            ? `Run · ${moment(managing_live.run_date, 'YYYY-MM-DD').format('ddd D MMM YYYY')}`
            : 'Run'
        }
      >
        {managing_live && (
          <div style={{ minWidth: 360 }}>
            <RunCard
              key={`${managing_live.run_uuid}-${sync_key}`}
              run={managing_live}
              busy={loading}
              embedded
              onSave={save_run}
              onPublish={publish}
              onCancelRun={setConfirmingCancel}
              onCancelSlot={setConfirmingSlotCancel}
              onDuplicate={open_duplicate}
              onDone={() => setManagingRun(null)}
              onDirtyChange={setEditorDirty}
            />
          </div>
        )}
      </CustomModal>

      <CustomModal show={editing_topic} close={() => setEditingTopic(false)} title="Edit event">
        <div className="p-2" style={{ minWidth: 360 }}>
          <label
            className="block text-sm mb-1"
            style={{ color: 'var(--text-secondary)' }}
            htmlFor="topic-name"
          >
            Name
            <input
              id="topic-name"
              value={topic_name}
              onChange={(e) => setTopicName(e.target.value)}
              className="w-full px-2 py-1 rounded"
              style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                color: 'var(--input-text)'
              }}
            />
          </label>

          <div className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Slug
          </div>
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {topic.slug}
          </div>
          {/* Blocked rather than disabled-with-no-explanation: the host needs to
              know why, not just that they cannot. */}
          <div className="mt-1 text-xs" style={{ color: 'var(--state-warning-text)' }}>
            The slug cannot be changed. Any live landing page points at the old value and would
            break.
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="ct-small-btn" onClick={() => setEditingTopic(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="ct-small-btn"
              onClick={submit_topic_name}
              disabled={!topic_name.trim() || loading}
            >
              Save
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal show={adding_run} close={() => setAddingRun(false)} title="Add run">
        <div className="p-2" style={{ minWidth: 320 }}>
          <label
            className="block text-sm mb-1"
            style={{ color: 'var(--text-secondary)' }}
            htmlFor="run-date"
          >
            Date (IST)
            <input
              id="run-date"
              type="date"
              value={new_run_date}
              onChange={(e) => setNewRunDate(e.target.value)}
              className="w-full px-2 py-1 rounded mb-3"
              style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                color: 'var(--input-text)'
              }}
            />
          </label>

          <label
            className="block text-sm mb-1"
            style={{ color: 'var(--text-secondary)' }}
            htmlFor="run-duration"
          >
            Duration
            <select
              id="run-duration"
              value={new_run_duration}
              onChange={(e) => setNewRunDuration(Number(e.target.value))}
              className="w-full px-2 py-1 rounded"
              style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                color: 'var(--input-text)'
              }}
            >
              {[30, 45, 60, 90].map((mins) => (
                <option key={mins} value={mins}>
                  {mins} min
                </option>
              ))}
            </select>
          </label>

          <div className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            Created as a Draft. Add slots and meeting links on the card, then Publish.
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="ct-small-btn" onClick={() => setAddingRun(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="ct-small-btn"
              onClick={submit_new_run}
              disabled={!new_run_date || loading}
            >
              Add
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal show={!!duplicating} close={() => setDuplicating(null)} title="Duplicate run">
        <div className="p-2" style={{ minWidth: 320 }}>
          <div className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
            Up to {MAX_DUPLICATE_DATES} dates. Each becomes its own Draft run with the same slot
            times.
          </div>
          {/* Links are not copied, and a host who does not know that will publish
              nothing. Said here rather than discovered on the new cards. */}
          <div className="text-xs mb-3" style={{ color: 'var(--state-warning-text)' }}>
            Meeting links are not copied — each session needs its own Zoom meeting.
          </div>

          {duplicate_dates.map((value, index) => (
            <input
              // Index is the identity: these are positional empty slots, not entities.
              key={`dup-${index}`}
              type="date"
              min={today_iso()}
              value={value}
              onChange={(e) => set_duplicate_date(index, e.target.value)}
              className="w-full px-2 py-1 rounded mb-2"
              style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                color: 'var(--input-text)'
              }}
            />
          ))}

          {duplicate_dates_error && (
            <div className="text-xs mb-2" style={{ color: 'var(--state-error-text)' }}>
              {duplicate_dates_error}
            </div>
          )}

          {duplicate_dates.length < MAX_DUPLICATE_DATES && (
            <button
              type="button"
              onClick={() => setDuplicateDates((prev) => [...prev, ''])}
              className="text-sm"
              style={{ color: 'var(--state-info)' }}
            >
              + Add date
            </button>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="ct-small-btn" onClick={() => setDuplicating(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="ct-small-btn"
              onClick={submit_duplicate}
              disabled={!!duplicate_dates_error || loading}
            >
              Duplicate
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        show={!!confirming_slot_cancel}
        close={() => setConfirmingSlotCancel(null)}
        title="Cancel slot"
      >
        <div className="p-2" style={{ minWidth: 320 }}>
          <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
            Cancel the{' '}
            {confirming_slot_cancel &&
              moment(confirming_slot_cancel.start_utc).tz(IST_ZONE).format('h:mm A')}{' '}
            IST slot?
          </div>
          <div className="mt-2 text-sm" style={{ color: 'var(--state-warning-text)' }}>
            {confirming_slot_cancel?.registration_count > 0 && (
              <>
                {confirming_slot_cancel.registration_count} person
                {confirming_slot_cancel.registration_count === 1 ? ' is' : 's are'} already
                registered.{' '}
              </>
            )}
            The API stops serving this slot immediately, and anyone already registered is notified —
            unless their session has already finished.
          </div>
          <div className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            Other slots on this run are unaffected.
          </div>
          {unsaved_edits_warning}

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              className="ct-small-btn"
              onClick={() => setConfirmingSlotCancel(null)}
            >
              Keep it
            </button>
            <button
              type="button"
              onClick={confirm_cancel_slot}
              className="px-3 py-1 rounded font-medium"
              style={{ background: 'var(--state-error)', color: 'var(--text-inverse)' }}
            >
              Cancel slot
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        show={!!confirming_delete}
        close={() => setConfirmingDelete(null)}
        title="Delete draft run"
      >
        <div className="p-2" style={{ minWidth: 320 }}>
          <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
            Delete the draft run on{' '}
            {confirming_delete &&
              moment(confirming_delete.run_date, 'YYYY-MM-DD').format('ddd D MMM YYYY')}
            ?
          </div>
          <div className="mt-2 text-sm" style={{ color: 'var(--state-warning-text)' }}>
            The run and its slots are removed for good. A draft was never served to the landing
            page, so nobody can have registered — but its activity entries stay, and will read as
            &quot;a run&quot; once it is gone.
          </div>
          {unsaved_edits_warning}

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              className="ct-small-btn"
              onClick={() => setConfirmingDelete(null)}
            >
              Keep it
            </button>
            <button
              type="button"
              onClick={confirm_delete_run}
              className="px-3 py-1 rounded font-medium"
              style={{ background: 'var(--state-error)', color: 'var(--text-inverse)' }}
            >
              Delete run
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        show={!!confirming_cancel}
        close={() => setConfirmingCancel(null)}
        title="Cancel run"
      >
        <div className="p-2" style={{ minWidth: 320 }}>
          <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
            Cancel the run on{' '}
            {confirming_cancel &&
              moment(confirming_cancel.run_date, 'YYYY-MM-DD').format('ddd D MMM YYYY')}
            ?
          </div>
          <div className="mt-2 text-sm" style={{ color: 'var(--state-warning-text)' }}>
            Its slots are cancelled too, and the API stops serving them to the landing page
            immediately. Anyone already registered is notified — unless their session has already
            finished, or their slot was cancelled earlier.
          </div>
          {unsaved_edits_warning}

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              className="ct-small-btn"
              onClick={() => setConfirmingCancel(null)}
            >
              Keep it
            </button>
            <button
              type="button"
              onClick={confirm_cancel_run}
              className="px-3 py-1 rounded font-medium"
              style={{ background: 'var(--state-error)', color: 'var(--text-inverse)' }}
            >
              Cancel run
            </button>
          </div>
        </div>
      </CustomModal>
    </CommonHeader>
  );
}

EventDetail.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ slug: PropTypes.string }) }).isRequired,
  history: PropTypes.shape({ push: PropTypes.func }).isRequired
};
